import { INTERIOR_WOOD_SPECIES, type InteriorWoodSpecies } from "../finishes/types";
/**
 * Structural validation for a layout arriving from outside the type system.
 *
 * `interior_layout` is a jsonb column and a share-URL field, so the object can
 * be anything: a row written by an older client, a hand-edited admin row, a
 * truncated payload, a crafted URL. The type assertion at the boundary used to
 * be the only check, which meant a layout missing `benchRuns` typechecked its
 * way into `resolveEffectiveLayout` and threw there — a corrupt row taking down
 * the whole calculation rather than falling back to the generated default.
 *
 * The migration deliberately adds no DB-side shape constraint (a stored layout
 * is meant to be repaired against the current room, not rejected), so this is
 * where the shape is established.
 *
 * Repair over rejection, but only where repair is meaningful: a single unusable
 * run is dropped and the rest of the layout survives, while a missing or
 * malformed heater means there is no layout to speak of and the caller gets
 * `null` — which is the documented "not authored" state the engines generate
 * for, not an error.
 */
import type {
	BenchRun,
	BenchSupportMode,
	BenchTier,
	HeaterPlacement,
	InteriorLayout,
	InteriorLayoutVariantId,
	InteriorRoomId,
	InteriorWall,
} from "./types";

const WALLS: readonly InteriorWall[] = ["front", "back", "left", "right"];
const TIERS: readonly BenchTier[] = ["upper", "lower"];
const ROOM_IDS: readonly InteriorRoomId[] = ["sauna", "relax"];
const VARIANTS: readonly InteriorLayoutVariantId[] = [
	"straight",
	"two-tier",
	"l-shape",
	"u-shape",
];

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Finite numbers only — `NaN`/`Infinity` survive JSON round-trips as `null`. */
function finiteNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function oneOf<T extends string>(
	value: unknown,
	allowed: readonly T[],
): T | null {
	return typeof value === "string" &&
		(allowed as readonly string[]).includes(value)
		? (value as T)
		: null;
}

function normalizeRun(value: unknown, index: number): BenchRun | null {
	if (!isRecord(value)) return null;

	const wall = oneOf(value.wall, WALLS);
	const tier = oneOf(value.tier, TIERS);
	const startMm = finiteNumber(value.startMm);
	const lengthMm = finiteNumber(value.lengthMm);
	const depthMm = finiteNumber(value.depthMm);
	if (wall === null || tier === null) return null;
	if (startMm === null || lengthMm === null || depthMm === null) return null;
	if (lengthMm <= 0 || depthMm <= 0 || startMm < 0) return null;

	return {
		// A run with no usable id still describes a real bench; give it one
		// rather than dropping the geometry.
		id: typeof value.id === "string" && value.id ? value.id : `bench-${index}`,
		room: oneOf(value.room, ROOM_IDS) ?? "sauna",
		wall,
		tier,
		startMm,
		lengthMm,
		depthMm,
		offsetFromWallMm: Math.max(0, finiteNumber(value.offsetFromWallMm) ?? 0),
		...(typeof value.heightGroupId === "string" && value.heightGroupId.trim()
			? { heightGroupId: value.heightGroupId.trim() } : {}),
		...Object.fromEntries(["skirtEnabled", "backrestEnabled"].flatMap((key) =>
			typeof value[key] === "boolean" ? [[key, value[key]]] : [])),
		...(oneOf<InteriorWoodSpecies>(value.woodSpecies, INTERIOR_WOOD_SPECIES) ? { woodSpecies: value.woodSpecies as InteriorWoodSpecies } : {}),
		...Object.fromEntries(["slatGapMm", "backrestStartMm", "backrestLengthMm", "backrestAboveSeatMm", "backrestBoardCount"].flatMap((key) =>
			finiteNumber(value[key]) === null ? [] : [[key, value[key]]])),
		...(finiteNumber(value.seatHeightMm) === null
			? {}
			: { seatHeightMm: value.seatHeightMm as number }),
		...(oneOf<BenchSupportMode>(value.supportMode, ["floor", "wall", "wall+frontLegs", "endWalls"])
			? { supportMode: value.supportMode as BenchSupportMode } : {}),
		// Absent means "use the tier's default", which is not the same as 0.
		...(finiteNumber(value.legHeightMm) === null
			? {}
			: { legHeightMm: Math.max(0, value.legHeightMm as number) }),
	};
}

function normalizeHeater(value: unknown): HeaterPlacement | null {
	if (!isRecord(value)) return null;
	const wall = oneOf(value.wall, WALLS);
	const offsetMm = finiteNumber(value.offsetMm);
	const wallGapMm = finiteNumber(value.wallGapMm);
	if (wall === null || offsetMm === null || wallGapMm === null) return null;
	return { wall, offsetMm, wallGapMm: Math.max(0, wallGapMm) };
}

/**
 * Parse an untrusted value into a layout, or `null` when it cannot describe
 * one. Never throws.
 */
export function normalizeInteriorLayout(value: unknown): InteriorLayout | null {
	if (!isRecord(value)) return null;

	const heater = normalizeHeater(value.heater);
	if (!heater) return null;
	if (!Array.isArray(value.benchRuns)) return null;

	const benchRuns: BenchRun[] = [];
	for (const [index, entry] of value.benchRuns.entries()) {
		const run = normalizeRun(entry, index);
		if (run) benchRuns.push(run);
	}

	return {
		// The variant is a label for where the layout started; the runs carry the
		// truth, so an unknown one is renamed rather than fatal.
		variant: oneOf(value.variant, VARIANTS) ?? "two-tier",
		benchRuns,
		heater,
	};
}
